/**
 * What the kids desk hears, where it hears it, and how it recovers when it
 * stopped hearing anything.
 *
 * ## The events that exist
 *
 * `realtime.service.ts` is the authority and it is narrower than it looks.
 * `onResourceSessionEvent` fans **five** names — started, paused, extended,
 * ended, expiring — to `branch:<branchId>` and `resource:<resourceId>`, and that
 * is the whole of what a kids area emits. Every one of them is subscribed to
 * below; nothing else is, because nothing else fires:
 *
 *  - **`resource_session.expiring` is real.** `sweepExpiring()` runs every
 *    minute, finds sessions inside the 10-minute horizon and emits at the 10 and
 *    5 minute marks with `remainingSeconds` on the payload. It also notifies the
 *    *guardian* — a person, off the premises perhaps — which is a different
 *    audience from the desk. The desk gets the socket frame.
 *  - **The waiting list emits nothing at all.** Its four actions are `AuditLog`
 *    appends, and the one notification it raises (`call`) is addressed to a
 *    guardian, which `onNotification` drops for want of a `userId`. The queue
 *    is therefore polled — see `WAITING_LIST_POLL_MS` — and refetched on every
 *    session event, because ending a session auto-calls the next family.
 *  - There is no `kids_area.*` event, no check-in event and no occupancy event.
 *    A check-in *is* `resource_session.started`, which is why that one is enough.
 *
 * ## Where
 *
 * `resource:<id>` first, spelled out rather than defaulted. `useRealtime`
 * defaults to `branch:<activeBranchId>` — the branch in the global header — and
 * this screen is keyed on a **resource**, picked from a list that is not
 * filtered by that header at all. A desk terminal showing an area in branch B
 * while the header says branch A would join `branch:A` and receive nothing:
 * five live subscriptions, all silent, with a LIVE pill above them.
 * `room-authorization.ts` grants `resource:` to any scope that covers the
 * resource, so a branch-scoped attendant holds it.
 *
 * `branch:` is kept alongside it only when a branch is known — it costs one
 * room, and it is the room every other screen already holds, so the socket is
 * usually joined to it anyway.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { VENUE_EVENTS, type ResourceSessionEventPayload } from '@/lib/shared-types';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import {
  getSocket,
  onRealtimeAuthFailure,
  onSocketChange,
  realtimeAuthFailure,
  socketGeneration,
  type RealtimeAuthFailure,
} from '@/lib/realtime';

/** Every event a kids area can produce, and no other. */
export const KIDS_STATION_EVENTS = [
  VENUE_EVENTS.RESOURCE_SESSION_STARTED,
  VENUE_EVENTS.RESOURCE_SESSION_PAUSED,
  VENUE_EVENTS.RESOURCE_SESSION_EXTENDED,
  VENUE_EVENTS.RESOURCE_SESSION_ENDED,
  VENUE_EVENTS.RESOURCE_SESSION_EXPIRING,
] as const;

export function kidsStationRooms(
  resourceId: string | null,
  branchId: string | null,
): string[] {
  return [
    ...(resourceId ? [`resource:${resourceId}`] : []),
    ...(branchId ? [`branch:${branchId}`] : []),
  ];
}

export interface KidsStationLive {
  /** Whether the socket is up *right now*. False means the board is a photograph. */
  connected: boolean;
  /** Set when realtime gave up for good; a human has to ask for another try. */
  authFailure: RealtimeAuthFailure | null;
}

export interface KidsStationLiveOptions {
  resourceId: string | null;
  branchId: string | null;
  /** A session in this area moved. Called with the payload that says so. */
  onSessionEvent: (payload: ResourceSessionEventPayload) => void;
  /** A child is at the 10- or 5-minute mark. Separate, because it is an alert. */
  onExpiring: (payload: ResourceSessionEventPayload) => void;
  /** The socket came back after a gap. Everything on screen is unverified. */
  onResync: () => void;
}

export function useKidsStationLive(options: KidsStationLiveOptions): KidsStationLive {
  const { resourceId, branchId } = options;
  const rooms = useMemo(
    () => kidsStationRooms(resourceId, branchId),
    [resourceId, branchId],
  );

  // Held in a ref so a caller writing inline arrows does not re-subscribe every
  // render — `useRealtime` keys its effect on the event and the rooms, and a new
  // handler identity would otherwise be invisible to it anyway.
  const handlers = useRef(options);
  handlers.current = options;

  /**
   * An event carries the resource it happened on, and this desk is one area.
   * Filtering here rather than trusting the room: `branch:` carries every
   * resource in the venue, so without this a PS5 timer three rooms away would
   * refetch the ball pit.
   */
  const onSession = useCallback(
    (payload: ResourceSessionEventPayload) => {
      if (resourceId && payload.resourceId !== resourceId) return;
      handlers.current.onSessionEvent(payload);
    },
    [resourceId],
  );

  const onExpiring = useCallback(
    (payload: ResourceSessionEventPayload) => {
      if (resourceId && payload.resourceId !== resourceId) return;
      handlers.current.onExpiring(payload);
      handlers.current.onSessionEvent(payload);
    },
    [resourceId],
  );

  useRealtime<ResourceSessionEventPayload>(
    VENUE_EVENTS.RESOURCE_SESSION_STARTED,
    onSession,
    rooms,
  );
  useRealtime<ResourceSessionEventPayload>(
    VENUE_EVENTS.RESOURCE_SESSION_PAUSED,
    onSession,
    rooms,
  );
  useRealtime<ResourceSessionEventPayload>(
    VENUE_EVENTS.RESOURCE_SESSION_EXTENDED,
    onSession,
    rooms,
  );
  useRealtime<ResourceSessionEventPayload>(
    VENUE_EVENTS.RESOURCE_SESSION_ENDED,
    onSession,
    rooms,
  );
  useRealtime<ResourceSessionEventPayload>(
    VENUE_EVENTS.RESOURCE_SESSION_EXPIRING,
    onExpiring,
    rooms,
  );

  /**
   * Come back from a blip with the truth.
   *
   * Everything above patches nothing — it invalidates — and none of it runs
   * while the socket is down. Socket.IO replays no events, so a two-minute
   * reconnect over a busy afternoon leaves a board that stopped being true two
   * minutes ago: children shown who have gone home, and, far worse, a queue that
   * has moved on. `onResync` is the same refetch the manual button fires.
   */
  const resync = useCallback(() => handlers.current.onResync(), []);
  useRealtimeResync(resync);

  /**
   * The socket's identity, not just its state — the same dependency
   * `useRealtime` takes, and for the same reason.
   *
   * `connect`/`disconnect` were bound once to whatever `getSocket()` returned at
   * mount. But the socket OBJECT is replaced: `onAuthFailure` drops the dead one
   * so the next caller is not handed a corpse, and a human pressing Reconnect
   * builds another. The listeners stayed on the old object, so `connected` froze
   * at whatever it last was — false — while the new socket connected, joined its
   * rooms and delivered events perfectly well.
   *
   * That is a worse failure than it sounds. The board goes on updating, and
   * above it sits a permanent RECONNECTING banner telling the attendant not to
   * trust what they are reading before handing over a child. A warning that is
   * always on is a warning nobody can act on, and it was pointed at the one
   * screen where being unsure has to mean something.
   */
  const generation = useSyncExternalStore(onSocketChange, socketGeneration, socketGeneration);

  const [connected, setConnected] = useState(() => getSocket().connected);
  useEffect(() => {
    const socket = getSocket();
    const up = () => setConnected(true);
    const down = () => setConnected(false);
    socket.on('connect', up);
    socket.on('disconnect', down);
    // Re-read after binding: the replacement may already have connected between
    // the render that saw the new generation and this effect running.
    setConnected(socket.connected);
    return () => {
      socket.off('connect', up);
      socket.off('disconnect', down);
    };
  }, [generation]);

  const authFailure = useSyncExternalStore(
    onRealtimeAuthFailure,
    realtimeAuthFailure,
    realtimeAuthFailure,
  );

  return { connected, authFailure };
}
